import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styles from "../css/Login.module.css";

const MINIMUM_PASSWORD_LENGTH = 8;

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "error", message: "" });

  const showError = (message) => setToast({ open: true, severity: "error", message });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      showError(`Your password must be at least ${MINIMUM_PASSWORD_LENGTH} characters long.`);
      return;
    }

    if (password !== confirmPassword) {
      showError("The passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        throw new Error("The reset link is invalid or has expired.");
      }

      setIsComplete(true);
    } catch (error) {
      showError("This reset link is invalid or has expired. Please request a new one.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container__login} aria-label="Reset password page">
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast.severity} variant="filled">{toast.message}</Alert>
      </Snackbar>
      <div className={styles.container__company}>
        <span className={styles.label__company}> by</span>
        <img className={styles.logo__company} src="/assets/img/logo_dicapta.png" alt="Dicapta" />
      </div>
      <div className={styles.container}>
        <div className={styles.header}>
          <figure className={styles.logo}>
            <img src="/assets/img/LogoLogin.png" alt="All 4 Voicing" />
          </figure>
        </div>
        <main className={styles.main}>
          {!token ? (
            <section className={styles.authMessage} aria-live="polite">
              <h1>Invalid reset link</h1>
              <p>This password reset link is missing or invalid.</p>
              <Link to="/forgot-password" className={styles.backToLogin}>Request a new link</Link>
            </section>
          ) : isComplete ? (
            <section className={styles.authMessage} aria-live="polite">
              <h1>Password updated</h1>
              <p>Your password has been reset successfully. You can now log in with your new password.</p>
              <Link to="/login" className={styles.backToLogin}>Go to log in</Link>
            </section>
          ) : (
            <form className={styles.container__formGroup} onSubmit={handleSubmit}>
              <section className={styles.authIntro}>
                <h1>Create a new password</h1>
                <p>Choose a password with at least {MINIMUM_PASSWORD_LENGTH} characters.</p>
              </section>
              <div className={styles.formGroup}>
                <label htmlFor="new-password" className={styles.formGroup__label}>New password</label>
                <input
                  type="password"
                  id="new-password"
                  name="newPassword"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={styles.formGroup__input}
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={MINIMUM_PASSWORD_LENGTH}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirm-password" className={styles.formGroup__label}>Confirm password</label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className={styles.formGroup__input}
                  required
                  autoComplete="new-password"
                  minLength={MINIMUM_PASSWORD_LENGTH}
                />
              </div>
              <div className={[styles.formGroup, styles.save].join(" ")}>
                <div className={styles.container__btnLogin}>
                  <button type="submit" className={styles.btnLogin} disabled={isSubmitting}>
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Reset password"}
                  </button>
                  <Link to="/login" className={styles.forgotPassword}>Back to log in</Link>
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};
